import { createContext, MouseEvent, ReactNode, useCallback, useContext, useRef, useState } from "react";
import {
	Box,
	CircularProgress,
	Divider,
	IconButton,
	Stack,
	Tooltip,
	Typography,
} from "@mui/material";
import { EditOutlined, InfoOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/useAuth";
import { Util } from "../util/Util";
import ItemDetailModal, { CatalogItemRow } from "../features/inventory/ItemDetailModal";

export type CatalogDepotPrice = {
	depot_id: number;
	depot_name: string;
	cost_price: number;
	selling_price: number;
	min_sell_price: number;
	stock?: number;
};

export type CatalogItemInfo = CatalogItemRow & {
	depot_prices?: CatalogDepotPrice[];
};

type CacheEntry = {
	at: number;
	info: CatalogItemInfo;
	depotPrices: CatalogDepotPrice[];
};

const INFO_CACHE_MS = 60_000;
const infoCache = new Map<string, CacheEntry>();

function cacheKey(itemId: number, depotIds: number[]): string {
	return `${itemId}:${depotIds.join(",")}`;
}

function normalizeDepotIds(depotIds?: Array<number | string | null | undefined>): number[] {
	const seen = new Set<number>();
	const ids: number[] = [];
	for (const raw of depotIds || []) {
		const id = Number(raw);
		if (!Number.isFinite(id) || id < 1 || seen.has(id)) continue;
		seen.add(id);
		ids.push(id);
	}
	return ids;
}

export function applySavedCatalogOption<T extends { id?: number | string | null }>(
	items: T[],
	updated: CatalogItemRow,
): T[] {
	return items.map((entry) => {
		if (Number(entry.id) !== Number(updated.id)) return entry;
		return {
			...entry,
			ItemName: updated.ItemName,
			ItemCode: updated.ItemCode,
			CostPrice: updated.CostPrice,
			SellingPrice: updated.SellingPrice,
			MinSellPrice: updated.MinSellPrice,
			Category: updated.Category ?? (entry as { Category?: string }).Category,
			Family: updated.Family ?? (entry as { Family?: string }).Family,
			Sector: updated.Sector ?? (entry as { Sector?: string }).Sector,
			IsMetered: updated.IsMetered,
			IsFractional: updated.IsFractional,
			IsActive: updated.IsActive,
		};
	});
}

function depotPriceSignature(row: CatalogDepotPrice): string {
	return `${row.cost_price}|${row.selling_price}|${row.min_sell_price}`;
}

function depotPricesDiffer(rows: CatalogDepotPrice[]): boolean {
	if (rows.length < 2) return false;
	return new Set(rows.map(depotPriceSignature)).size > 1;
}

function InfoRow({ label, value }: { label: string; value: ReactNode }) {
	return (
		<Box sx={{ display: "flex", justifyContent: "space-between", gap: 1.5, alignItems: "baseline" }}>
			<Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
				{label}
			</Typography>
			<Typography variant="caption" sx={{ fontWeight: 600, textAlign: "right" }}>
				{value}
			</Typography>
		</Box>
	);
}

function ItemInfoCard({
	loading,
	error,
	info,
	depotPrices,
	redactPosFields = false,
}: {
	loading: boolean;
	error: boolean;
	info: CatalogItemInfo | null;
	depotPrices: CatalogDepotPrice[];
	redactPosFields?: boolean;
}) {
	const { t } = useTranslation("common");

	if (loading || (!info && !error)) {
		return (
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
				<CircularProgress size={14} />
				<Typography variant="caption">{t("catalog.itemInfoLoading")}</Typography>
			</Box>
		);
	}

	if (error || !info) {
		return (
			<Typography variant="caption">{t("catalog.itemInfoFailed")}</Typography>
		);
	}

	const showDepotPrices = depotPricesDiffer(depotPrices);
	const showDepotStock = depotPrices.length > 0 && info.IsMetered !== false;

	return (
		<Stack spacing={0.75} sx={{ minWidth: 240, maxWidth: 360 }}>
			<Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
				{info.ItemName || "—"}
			</Typography>
			<InfoRow label={t("tables.itemCode")} value={info.ItemCode || "—"} />
			<InfoRow label={t("tables.category")} value={info.Category || "—"} />
			<InfoRow label={t("tables.family")} value={info.Family || "—"} />
			<InfoRow label={t("tables.sector")} value={info.Sector || "—"} />
			<Divider />
			<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
				{t("catalog.catalogPrices")}
			</Typography>
			{!redactPosFields && (
				<InfoRow label={t("tables.costPrice")} value={Util.formatNumber(Number(info.CostPrice ?? 0))} />
			)}
			<InfoRow label={t("tables.sellingPrice")} value={Util.formatNumber(Number(info.SellingPrice ?? 0))} />
			<InfoRow label={t("productCatalog.minSellPrice")} value={Util.formatNumber(Number(info.MinSellPrice ?? 0))} />
			{!redactPosFields && (
				<InfoRow label={t("productCatalog.minOrderQty")} value={Util.formatNumber(Number(info.MinOrderQty ?? 0))} />
			)}
			{!redactPosFields && (
				<InfoRow
					label={t("productCatalog.metered")}
					value={info.IsMetered === false ? t("ui.labels.no") : t("ui.labels.yes")}
				/>
			)}
			{!redactPosFields && (
				<InfoRow
					label={t("productCatalog.fractional")}
					value={info.IsFractional === true ? t("ui.labels.yes") : t("ui.labels.no")}
				/>
			)}
			<InfoRow
				label={t("productCatalog.active")}
				value={info.IsActive === false ? t("productCatalog.inactive") : t("productCatalog.active")}
			/>
			{info.barcodes && info.barcodes.length > 0 && (
				<InfoRow label={t("productCatalog.barcodes")} value={info.barcodes.join(", ")} />
			)}
			{showDepotPrices && (
				<>
					<Divider />
					<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
						{t("catalog.depotPrices")}
					</Typography>
					{depotPrices.map((depot) => (
						<Box key={depot.depot_id} sx={{ pl: 0.25 }}>
							<Typography variant="caption" sx={{ fontWeight: 700 }}>
								{depot.depot_name}
							</Typography>
							{!redactPosFields && (
								<InfoRow label={t("tables.costPrice")} value={Util.formatNumber(depot.cost_price)} />
							)}
							<InfoRow label={t("tables.sellingPrice")} value={Util.formatNumber(depot.selling_price)} />
							<InfoRow label={t("productCatalog.minSellPrice")} value={Util.formatNumber(depot.min_sell_price)} />
							{showDepotStock && (
								<InfoRow label={t("tables.stock")} value={Util.formatNumber(depot.stock ?? 0)} />
							)}
						</Box>
					))}
				</>
			)}
			{!showDepotPrices && showDepotStock && (
				<>
					<Divider />
					<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
						{t("catalog.depotStock")}
					</Typography>
					{depotPrices.map((depot) => (
						<InfoRow
							key={depot.depot_id}
							label={depot.depot_name}
							value={Util.formatNumber(depot.stock ?? 0)}
						/>
					))}
				</>
			)}
		</Stack>
	);
}

type CatalogItemPickerContextValue = {
	depotIds: number[];
	openEdit: (item: CatalogItemRow) => void;
};

const CatalogItemPickerContext = createContext<CatalogItemPickerContextValue | null>(null);

export function useCatalogItemPicker(): CatalogItemPickerContextValue | null {
	return useContext(CatalogItemPickerContext);
}

interface CatalogItemPickerProviderProps {
	children: ReactNode;
	depotIds?: Array<number | string | null | undefined>;
	onItemSaved?: (item: CatalogItemRow) => void;
}

/** Holds the catalog edit modal outside the Autocomplete list so it survives list close. */
export function CatalogItemPickerProvider({
	children,
	depotIds,
	onItemSaved,
}: CatalogItemPickerProviderProps) {
	const depots = normalizeDepotIds(depotIds);
	const [editItem, setEditItem] = useState<CatalogItemRow | null>(null);
	const [editOpen, setEditOpen] = useState(false);

	const openEdit = useCallback((item: CatalogItemRow) => {
		setEditItem(item);
		setEditOpen(true);
	}, []);

	return (
		<CatalogItemPickerContext.Provider value={{ depotIds: depots, openEdit }}>
			{children}
			<ItemDetailModal
				isOpen={editOpen}
				toggle={() => setEditOpen(false)}
				item={editItem}
				onItemSaved={(updated) => {
					for (const key of Array.from(infoCache.keys())) {
						if (key.startsWith(`${updated.id}:`)) infoCache.delete(key);
					}
					onItemSaved?.(updated);
					setEditOpen(false);
				}}
			/>
		</CatalogItemPickerContext.Provider>
	);
}

function stopOptionSelect(event: MouseEvent) {
	event.preventDefault();
	event.stopPropagation();
}

/** Info + edit icons for a dropdown option or table row. Parent should show these on hover. */
export function CatalogItemOptionActions({
	itemId,
	itemName,
	isPos = false,
	showEdit = true,
	minWidth,
}: {
	itemId?: number | string | null;
	itemName?: string;
	isPos?: boolean;
	showEdit?: boolean;
	minWidth?: number;
}) {
	const { t } = useTranslation("common");
	const { api } = useAuth();
	const picker = useCatalogItemPicker();
	const resolvedId = Number(itemId);
	const hasItem = Number.isFinite(resolvedId) && resolvedId > 0;
	const depots = picker?.depotIds ?? [];

	const [infoOpen, setInfoOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(false);
	const [info, setInfo] = useState<CatalogItemInfo | null>(null);
	const [loadedDepots, setLoadedDepots] = useState<CatalogDepotPrice[]>([]);
	const fetchGen = useRef(0);

	const loadInfo = useCallback(async () => {
		if (!hasItem) return;
		const key = cacheKey(resolvedId, depots);
		const cached = infoCache.get(key);
		if (cached && Date.now() - cached.at < INFO_CACHE_MS) {
			setInfo(cached.info);
			setLoadedDepots(cached.depotPrices);
			setError(false);
			setLoading(false);
			return;
		}

		const gen = ++fetchGen.current;
		setLoading(true);
		setError(false);
		try {
			const params = depots.length ? { depots: depots.join(",") } : undefined;
			const res = await api.get(`items/master/${resolvedId}`, { params });
			if (gen !== fetchGen.current) return;
			if (res?.status === 200 && res.data?.item) {
				const next = res.data.item as CatalogItemInfo;
				const nextDepots = Array.isArray(res.data.depot_prices)
					? (res.data.depot_prices as CatalogDepotPrice[])
					: [];
				infoCache.set(key, { at: Date.now(), info: next, depotPrices: nextDepots });
				setInfo(next);
				setLoadedDepots(nextDepots);
				setError(false);
			} else {
				setError(true);
			}
		} catch {
			if (gen !== fetchGen.current) return;
			setError(true);
		} finally {
			if (gen === fetchGen.current) setLoading(false);
		}
	}, [api, depots, hasItem, resolvedId]);

	const [internalEditOpen, setInternalEditOpen] = useState(false);

	if (!hasItem) return null;

	const canEdit = !isPos && showEdit !== false;

	return (
		<Box
			className={`catalog-item-option-actions${infoOpen ? " catalog-item-option-actions--open" : ""}`}
			sx={{
				display: "inline-flex",
				alignItems: "center",
				justifyContent: "flex-end",
				flexShrink: 0,
				ml: 0.5,
				minWidth,
			}}
			onMouseDown={stopOptionSelect}
			onClick={stopOptionSelect}
		>
			<Tooltip
				arrow
				placement="left"
				enterDelay={200}
				enterNextDelay={120}
				leaveDelay={180}
				disableFocusListener
				onOpen={() => {
					setInfoOpen(true);
					void loadInfo();
				}}
				onClose={() => setInfoOpen(false)}
				slotProps={{
					popper: {
						onMouseDown: stopOptionSelect,
						onClick: stopOptionSelect,
					},
					tooltip: {
						sx: {
							bgcolor: "background.paper",
							color: "text.primary",
							border: 1,
							borderColor: "divider",
							boxShadow: 4,
							maxWidth: 400,
							p: 1.25,
						},
					},
				}}
				title={
					<ItemInfoCard
						loading={loading}
						error={error}
						info={info}
						depotPrices={loadedDepots}
						redactPosFields={isPos}
					/>
				}
			>
				<span>
					<IconButton
						type="button"
						size="small"
						tabIndex={-1}
						aria-label={t("catalog.itemInfo")}
						onMouseDown={stopOptionSelect}
						onClick={stopOptionSelect}
						sx={{ p: 0.25 }}
					>
						<InfoOutlined sx={{ fontSize: 16 }} />
					</IconButton>
				</span>
			</Tooltip>
			{canEdit && (
				<>
					<Tooltip title={t("catalog.editItem")} disableFocusListener>
						<span>
							<IconButton
								type="button"
								size="small"
								tabIndex={-1}
								aria-label={t("catalog.editItem")}
								onMouseDown={stopOptionSelect}
								onClick={(event) => {
									stopOptionSelect(event);
									if (picker) {
										picker.openEdit({
											id: resolvedId,
											ItemName: itemName,
										});
									} else {
										setInternalEditOpen(true);
									}
								}}
								sx={{ p: 0.25 }}
							>
								<EditOutlined sx={{ fontSize: 16 }} />
							</IconButton>
						</span>
					</Tooltip>
					{!picker && (
						<ItemDetailModal
							isOpen={internalEditOpen}
							toggle={() => setInternalEditOpen(false)}
							item={{ id: resolvedId, ItemName: itemName }}
							onItemSaved={(updated) => {
								for (const key of Array.from(infoCache.keys())) {
									if (key.startsWith(`${updated.id}:`)) infoCache.delete(key);
								}
								setInternalEditOpen(false);
							}}
						/>
					)}
				</>
			)}
		</Box>
	);
}

/** One small icon button: 16px glyph inside 0.25 spacing units of padding. */
const ACTION_BUTTON_WIDTH = 20;

/**
 * Item-name cell: the text, plus info/edit actions revealed on hover.
 *
 * The actions are not mounted until the pointer reaches the cell. A report page
 * renders every row of the current page, and at 500 or 1000 rows per page each
 * mounted action set costs two MUI Tooltips plus i18n and auth subscriptions —
 * far too much to pay for controls that are invisible until hover. The slot
 * keeps its width either way, so mounting never shifts the row.
 */
export function CatalogItemNameCell({
	itemId,
	itemName,
	isPos = false,
	showEdit = true,
	children,
}: {
	itemId?: number | string | null;
	itemName?: string;
	isPos?: boolean;
	showEdit?: boolean;
	children: ReactNode;
}) {
	const picker = useCatalogItemPicker();
	const [armed, setArmed] = useState(false);

	const resolvedId = Number(itemId);
	const hasItem = Number.isFinite(resolvedId) && resolvedId > 0;

	if (!hasItem) return <>{children}</>;

	// Matches the predicate CatalogItemOptionActions applies to the edit button.
	const canEdit = Boolean(picker) && !isPos && showEdit !== false;
	const slotWidth = ACTION_BUTTON_WIDTH * (canEdit ? 2 : 1);

	return (
		<Box
			onMouseEnter={() => setArmed(true)}
			onFocus={() => setArmed(true)}
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				width: "100%",
				minWidth: 0,
				"& .catalog-item-option-actions": {
					opacity: 0,
					pointerEvents: "none",
				},
				"&:hover .catalog-item-option-actions, & .catalog-item-option-actions--open": {
					opacity: 1,
					pointerEvents: "auto",
				},
			}}
		>
			<Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>
				{children}
			</Box>
			{armed ? (
				<CatalogItemOptionActions
					itemId={resolvedId}
					itemName={itemName}
					isPos={isPos}
					showEdit={showEdit}
					minWidth={slotWidth}
				/>
			) : (
				<Box aria-hidden sx={{ width: slotWidth, flexShrink: 0, ml: 0.5 }} />
			)}
		</Box>
	);
}

/** Info + edit action icons for table row hover. POS rows show only Info (redact cost/metered/min order). */
export function CatalogItemRowActions({
	itemId,
	itemName,
	isPos = false,
	showEdit = true,
}: {
	itemId?: number | string | null;
	itemName?: string;
	isPos?: boolean;
	showEdit?: boolean;
}) {
	return (
		<CatalogItemOptionActions
			itemId={itemId}
			itemName={itemName}
			isPos={isPos}
			showEdit={showEdit}
		/>
	);
}

